import React, { useEffect, useState, useCallback, useRef, Component, type ErrorInfo, type ReactNode } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, Trophy } from 'lucide-react';
import { ModuleService } from '@/services/ModuleService';
import { useModuleProgress } from '@/contexts/ModuleProgressContext';
import { LoadingState, ErrorState } from '@/components/custom-ui/empty-state';
import { ModuleProgressBar } from './ModuleProgressBar';
import { InfoStepRenderer } from './steps/InfoStepRenderer';
import { CheckpointStepRenderer } from './steps/CheckpointStepRenderer';
import { ConfirmationStepRenderer } from './steps/ConfirmationStepRenderer';
import { QuizStepRenderer } from './steps/QuizStepRenderer';
import { CaseStepRenderer } from './steps/CaseStepRenderer';
import { NEXILINK_CARD_WHITE, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';
import { explanationFor, type ModuleFullResponse, type ModuleStep, type StepResultResponse } from '@/lib/models/module';

// Debug error boundary to catch and display actual error message
class ModuleErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
    state: { error: Error | null } = { error: null };
    static getDerivedStateFromError(error: Error) { return { error }; }
    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[ModuleViewer ErrorBoundary]', error, info.componentStack);
    }
    render() {
        if (this.state.error) {
            return (
                <div className="p-6 bg-red-50 border-2 border-red-200 rounded-2xl">
                    <h3 className="font-bold text-red-900 mb-2">ModuleViewer Error</h3>
                    <pre className="text-sm text-red-700 whitespace-pre-wrap">{this.state.error.message}</pre>
                    <pre className="text-xs text-red-500 mt-2 whitespace-pre-wrap">{this.state.error.stack}</pre>
                </div>
            );
        }
        return this.props.children;
    }
}

// When rendered as a route, moduleId + preview come from the URL. When rendered
// in an overlay (employer "Visa som student"), the caller passes them as props
// plus onClose, and the back/exit controls call onClose instead of navigating.
interface ModuleViewerProps {
    moduleId?: string;
    preview?: boolean;
    onClose?: () => void;
    // Suppress the internal back-arrow (the surrounding chrome provides its own
    // exit, e.g. the full-bleed CoursePlayerPage top bar). Title still shows.
    chromeless?: boolean;
    // In-memory course data to render INSTEAD of fetching by id. Used by the
    // course editor's "Visa som student" so the preview reflects the live,
    // unsaved edits (name/description/steps/order) rather than the saved version.
    // Implies preview mode (nothing is persisted).
    previewData?: ModuleFullResponse;
}

// Client-side grading for PREVIEW mode ("Visa som student"), mirroring the
// backend validators (module_service.go) so an employer previewing a course gets
// real correct/incorrect feedback — not a blanket "correct". Covers every step
// type; text_input/ai_evaluated can't be graded offline so they're accepted, same
// as the server.
function gradeStepPreview(step: ModuleStep, answer: any, cases: Record<string, any>): StepResultResponse {
    const content: any = step.content || {};

    const gradeQuestions = (questions: any[], onlyMultipleChoice: boolean): number[] => {
        const answers = answer?.answers || {};
        const wrong: number[] = [];
        questions.forEach((q: any, idx: number) => {
            const ua = answers[String(idx)];
            switch (q?.question_type) {
                case 'multi_select': {
                    if (onlyMultipleChoice) break; // case steps grade MC only (matches BE)
                    const correctIds = (q.options || []).filter((o: any) => o.is_correct).map((o: any) => o.option_id).sort();
                    const sel = Array.isArray(ua) ? [...ua].map(String).sort() : [];
                    if (correctIds.length !== sel.length || correctIds.some((id: string, i: number) => id !== sel[i])) wrong.push(idx);
                    break;
                }
                case 'confirmation':
                    if (onlyMultipleChoice) break;
                    if (String(ua) !== 'true') wrong.push(idx);
                    break;
                case 'text_input':
                case 'ai_evaluated':
                    break; // accepted (not auto-graded), same as the server
                default:
                    // multiple_choice AND anything unrecognised, including a
                    // question with no question_type (#313). Mirrors validateQuiz:
                    // input we cannot grade must not be reported as passed.
                    if (ua !== q.correct_option_id) wrong.push(idx);
            }
        });
        return wrong;
    };

    switch (step.step_type) {
        case 'checkpoint': {
            // #293 — an open answer has no verdict. Mirrors validateCheckpoint:
            // Correct stays undefined so the preview shows the employer the same
            // neutral "Svaret är sparat" a candidate sees, rather than grading
            // prose against an empty correct_option_id and inventing a result.
            if (content.question_type === 'text_input' || content.question_type === 'ai_evaluated') {
                return {
                    correct: undefined,
                    step_completed: true,
                    module_completed: false,
                    explanation: content.explanation,
                };
            }
            const correctID = content.correct_option_id;
            const correct = answer?.selected_option_id === correctID;
            // Checkpoint always advances; feedback shows whether it was right.
            return {
                correct,
                step_completed: true,
                module_completed: false,
                // #294 — mirror the server: pick the explanation that fits the outcome,
                // so "Visa som student" shows the employer exactly what a candidate gets.
                explanation: explanationFor(content, correct),
                correct_answer: correct ? undefined : correctID,
            };
        }
        case 'confirmation': {
            const confirmed = answer === true || answer?.confirmed === true;
            return { correct: confirmed, step_completed: confirmed, module_completed: false };
        }
        case 'quiz': {
            // Every question must be right — mirrors validateQuiz. pass_threshold
            // is deliberately NOT read here: it is not authorable, so enforcing it
            // would make the preview disagree with the editor's own help text.
            // Partial pass is #43, and it must be opt-in.
            const wrong = gradeQuestions(content.questions || [], false);
            const correct = wrong.length === 0;
            return { correct, wrong_questions: wrong, step_completed: correct, module_completed: false };
        }
        case 'case': {
            const cd = content.case_id ? cases[content.case_id] : undefined;
            const questions: any[] = Array.isArray(cd?.questions) ? cd.questions : [];
            const wrong = gradeQuestions(questions, true);
            const correct = wrong.length === 0;
            return { correct, wrong_questions: wrong, step_completed: correct, module_completed: false };
        }
        default:
            // info (and anything non-graded) — always complete.
            return { correct: true, step_completed: true, module_completed: false };
    }
}

const ModuleViewerInner: React.FC<ModuleViewerProps> = ({ moduleId: moduleIdProp, preview: previewProp, onClose, chromeless, previewData }) => {
    const params = useParams<{ moduleId: string }>();
    const moduleId = moduleIdProp ?? params.moduleId;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isPreview = previewData != null || (previewProp ?? (searchParams.get('preview') === 'true'));
    const { refreshIntroStatus } = useModuleProgress();

    // Exit/back: overlay → close; preview → back to the read/edit view; otherwise
    // return to WHEREVER the candidate launched the course from (a job posting's
    // prerequisites, onboarding, the Moduler list, …). Courses are entered the same
    // way from every context (/dashboard/modules/:id) and steps are state — not
    // routes — so a single history pop lands back on the entry page (e.g. a job
    // whose prerequisite is now satisfied). Fall back to the course list only when
    // there's no history to pop (hard refresh / deep-link straight into the player).
    const closeOrBack = () => {
        if (onClose) { onClose(); return; }
        if (isPreview) { navigate(`/dashboard/onboarding/modules/${moduleId}`); return; }
        const hasHistory = (window.history.state?.idx ?? 0) > 0;
        if (hasHistory) navigate(-1);
        else navigate('/dashboard/modules', { replace: true });
    };

    const [data, setData] = useState<ModuleFullResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentStepOrder, setCurrentStepOrder] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
    // What the candidate actually ANSWERED, per step order (#293).
    //
    // step_results was already loaded and then read only for its KEYS, to build
    // completedSteps — the stored answer was dropped on the floor. So navigating
    // back to an answered step re-mounted a pristine form: an empty textarea and
    // a disabled "Svara", which is indistinguishable from the answer having been
    // lost. It never was; it just was never handed to the renderer.
    //
    // Seeded from the server on load and kept current on every submit, so it
    // survives both a reload and back-navigation within the session (the server
    // copy is not refetched after submitting).
    const [stepAnswers, setStepAnswers] = useState<Record<number, any>>({});
    // True the moment the candidate finishes the final step (this session) or when
    // re-opening an already-completed course. Drives the celebration screen instead
    // of silently bouncing to the course list.
    const [justCompleted, setJustCompleted] = useState(false);
    // Walking back through a FINISHED module to re-read it and see what you
    // answered. Without this a completed module was a dead end: re-opening it
    // landed on the celebration screen, whose only control led away — so a
    // candidate could never see their own written answers again, even though
    // the employer can.
    const [isReviewing, setIsReviewing] = useState(false);
    // Crossfade + scroll-to-top between steps, so a new step always appears at the
    // top with a calm transition instead of a jarring in-place swap.
    const rootRef = useRef<HTMLDivElement>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);

    const loadModule = useCallback(async () => {
        // Live in-memory preview (course editor) — render the provided data as-is,
        // no fetch, start at the first step, nothing completed.
        if (previewData) {
            const result = { ...previewData };
            result.steps = result.steps || [];
            result.cases = result.cases || {};
            setData(result);
            const sorted = [...result.steps].sort((a, b) => a.step_order - b.step_order);
            setCurrentStepOrder(sorted.length ? sorted[0].step_order : 0);
            setCompletedSteps(new Set());
            setStepAnswers({});
            setJustCompleted(false);
            setError(null);
            setIsLoading(false);
            return;
        }
        if (!moduleId) return;
        setIsLoading(true);
        setError(null);
        try {
            const result = await ModuleService.getModuleFull(moduleId);
            if (!result) {
                setError('Modulen hittades inte.');
                return;
            }
            // Normalize: ensure steps is an array and cases is an object
            result.steps = result.steps || [];
            result.cases = result.cases || {};

            setData(result);

            const sorted = [...result.steps].sort((a, b) => a.step_order - b.step_order);

            // Build the completed-steps set from step_results (a key present means
            // that step was submitted — the same rule the progress bar uses).
            const completed = new Set<number>();
            const answers: Record<number, any> = {};
            if (result.progress?.step_results) {
                for (const [key, value] of Object.entries(result.progress.step_results)) {
                    completed.add(Number(key));
                    // Each entry is { completed, answer, submitted_at } — `answer`
                    // is the payload the renderer originally sent, so it can be
                    // handed straight back to hydrate the same form.
                    const saved = (value as any)?.answer;
                    if (saved != null) answers[Number(key)] = saved;
                }
            }
            setCompletedSteps(completed);
            setStepAnswers(answers);

            // Resume at the LOWEST unfinished step. This is more robust than the
            // stored current_step_order high-water mark: it survives edited/renumbered
            // steps and always lands a returning candidate on the first step they
            // haven't done. If every step is finished we fall back to the last step
            // and the completion screen (justCompleted) takes over.
            let stepOrder = 0;
            if (sorted.length > 0) {
                const firstUnfinished = sorted.find((s) => !completed.has(s.step_order));
                stepOrder = firstUnfinished
                    ? firstUnfinished.step_order
                    : sorted[sorted.length - 1].step_order;
            }
            setCurrentStepOrder(stepOrder);

            // Re-opening an already-finished course → go straight to the finale.
            setJustCompleted(
                sorted.length > 0 &&
                result.progress?.status === 'completed' &&
                sorted.every((s) => completed.has(s.step_order))
            );
        } catch (err) {
            setError('Kunde inte ladda modulen.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [moduleId]);

    useEffect(() => {
        loadModule();
    }, [loadModule]);

    const steps = data?.steps || [];
    const currentStep = steps.find((s) => s.step_order === currentStepOrder);

    const scrollToTop = () => {
        window.scrollTo({ top: 0 });
        // Overlay/preview mode scrolls an inner container rather than the window —
        // walk up to the nearest scrollable ancestor and reset it too.
        let el: HTMLElement | null = rootRef.current?.parentElement ?? null;
        while (el) {
            const oy = window.getComputedStyle(el).overflowY;
            if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
                el.scrollTop = 0;
                break;
            }
            el = el.parentElement;
        }
    };

    // Move to another step with a brief crossfade: fade the current step out, then
    // swap + scroll to top while it's invisible (so the jump isn't seen), then fade
    // the new step in.
    const changeStep = (order: number) => {
        if (order === currentStepOrder) return;
        setIsTransitioning(true);
        window.setTimeout(() => {
            setCurrentStepOrder(order);
            scrollToTop();
            requestAnimationFrame(() => requestAnimationFrame(() => setIsTransitioning(false)));
        }, 200);
    };

    // A finished course lands on the celebration screen — bring it into view.
    useEffect(() => {
        if (justCompleted) scrollToTop();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [justCompleted]);

    const handleStepSubmit = async (answer: any): Promise<StepResultResponse> => {
        if (!currentStep) {
            return { correct: false, step_completed: false, module_completed: false };
        }

        // If the answer has _advance flag, just move to next step locally
        if (answer?._advance) {
            advanceToNextStep();
            return { correct: true, step_completed: true, module_completed: false };
        }

        // Preview mode ("Visa som student"): grade locally (mirroring the backend)
        // so the employer sees real correct/incorrect feedback, but persist nothing.
        // Handled before the moduleId guard so live in-memory previews (course
        // editor, no saved id yet) still work.
        if (isPreview) {
            setStepAnswers((prev) => ({ ...prev, [currentStepOrder]: answer }));
            const graded = gradeStepPreview(currentStep, answer, data?.cases || {});
            if (!graded.step_completed) {
                // Wrong answer — stay on the step; the renderer shows feedback/retry.
                return { ...graded, module_completed: false };
            }
            setCompletedSteps((prev) => new Set([...prev, currentStepOrder]));
            const sorted = [...steps].sort((a, b) => a.step_order - b.step_order);
            const currentIdx = sorted.findIndex((s) => s.step_order === currentStepOrder);
            const nextStep = sorted[currentIdx + 1];
            const moduleCompleted = !nextStep;

            // Auto-advance for non-interactive step types
            if (nextStep && currentStep.step_type !== 'checkpoint' && currentStep.step_type !== 'quiz' && currentStep.step_type !== 'case') {
                changeStep(nextStep.step_order);
            }

            if (moduleCompleted) {
                setJustCompleted(true);
            }

            return {
                ...graded,
                module_completed: moduleCompleted,
                next_step_order: nextStep?.step_order,
            };
        }

        if (!moduleId) {
            return { correct: false, step_completed: false, module_completed: false };
        }

        setIsSubmitting(true);
        try {
            const result = await ModuleService.submitStepResult(moduleId, {
                step_order: currentStepOrder,
                answer,
            });

            console.log('[ModuleViewer] submitStepResult response:', result);

            // Mirror what the server just stored. Recorded even when the step was
            // NOT completed (a failed quiz), so a candidate sent back to retry
            // keeps the free-text paragraphs they already wrote.
            setStepAnswers((prev) => ({ ...prev, [currentStepOrder]: answer }));

            if (result.step_completed) {
                setCompletedSteps((prev) => new Set([...prev, currentStepOrder]));

                if (result.module_completed) {
                    await refreshIntroStatus();
                    // Show the celebration screen and let the candidate leave on
                    // their own terms, rather than yanking them to the list.
                    setJustCompleted(true);
                } else if (result.next_step_order != null) {
                    // For non-checkpoint types, auto-advance
                    if (currentStep.step_type !== 'checkpoint' && currentStep.step_type !== 'quiz' && currentStep.step_type !== 'case') {
                        changeStep(result.next_step_order);
                    }
                }
            }

            return result;
        } catch (err) {
            console.error('[ModuleViewer] Error submitting step:', err);
            setError('Kunde inte skicka svar. Försök igen.');
            return { correct: false, step_completed: false, module_completed: false };
        } finally {
            setIsSubmitting(false);
        }
    };

    const advanceToNextStep = () => {
        if (!data) return;
        const sorted = [...data.steps].sort((a, b) => a.step_order - b.step_order);
        const currentIdx = sorted.findIndex((s) => s.step_order === currentStepOrder);
        if (currentIdx < sorted.length - 1) {
            changeStep(sorted[currentIdx + 1].step_order);
        } else if (isReviewing) {
            // Reviewing a finished module — the end of the walk-through returns to
            // the completion screen it started from, rather than dumping the
            // candidate on the dashboard as if they had just finished it again.
            setIsReviewing(false);
            setJustCompleted(true);
        } else {
            // Last step — module is done. In overlay mode just close; otherwise route.
            if (onClose) { onClose(); return; }
            navigate(isPreview ? `/dashboard/onboarding/modules/${moduleId}` : '/dashboard', { replace: true });
        }
    };

    const goToPreviousStep = () => {
        if (!data) return;
        const sorted = [...data.steps].sort((a, b) => a.step_order - b.step_order);
        const currentIdx = sorted.findIndex((s) => s.step_order === currentStepOrder);
        if (currentIdx > 0) {
            changeStep(sorted[currentIdx - 1].step_order);
        }
    };

    const canGoBack = (() => {
        if (!data) return false;
        const sorted = [...data.steps].sort((a, b) => a.step_order - b.step_order);
        const currentIdx = sorted.findIndex((s) => s.step_order === currentStepOrder);
        return currentIdx > 0;
    })();

    const canGoForward = (() => {
        if (!data) return false;
        const sorted = [...data.steps].sort((a, b) => a.step_order - b.step_order);
        const currentIdx = sorted.findIndex((s) => s.step_order === currentStepOrder);
        if (currentIdx >= sorted.length - 1) return false;
        // Can only go forward to already-completed steps
        return completedSteps.has(sorted[currentIdx + 1].step_order);
    })();

    const goToStep = (stepOrder: number) => {
        // Allow navigating to completed steps or the current step
        if (completedSteps.has(stepOrder) || stepOrder === currentStepOrder) {
            changeStep(stepOrder);
        }
    };

    if (isLoading) return <LoadingState message="Laddar modul..." />;
    if (error) return <ErrorState title="Fel" message={error} onRetry={loadModule} />;
    if (!data) return <ErrorState title="Fel" message="Modulen kunde inte laddas." onRetry={loadModule} />;
    if (steps.length === 0) return <ErrorState title="Inga steg" message="Den här modulen har inga steg ännu." />;
    if (!currentStep) return <ErrorState title="Fel" message="Kunde inte hitta aktuellt steg." onRetry={loadModule} />;

    // ── Course finished — celebration takeover ───────────────────────
    if (justCompleted) {
        const stepCount = steps.length;
        const sortedSteps = [...steps].sort((a, b) => a.step_order - b.step_order);
        const startReview = () => {
            setIsReviewing(true);
            setJustCompleted(false);
            setCurrentStepOrder(sortedSteps[0].step_order);
            scrollToTop();
        };
        // Return the candidate to their entry context (job posting, onboarding,
        // course list, …) — same logic as the back button, so a prerequisite
        // completer lands back on the now-unlockable job, etc.
        const finishLabel = onClose ? 'Stäng' : isPreview ? 'Tillbaka till redigering' : 'Tillbaka';
        return (
            <div className={NEXILINK_SPACING}>
                <div className={`${NEXILINK_CARD_WHITE} p-10 text-center max-w-xl mx-auto`}>
                    <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-[#1A4D4F]/10">
                        <Trophy className="h-10 w-10 text-[#1A4D4F]" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Du är klar! 🎉</h2>
                    <p className="text-gray-700 mb-1 font-medium">"{data.module_name}"</p>
                    <p className="text-sm text-gray-500 mb-6">
                        Snyggt jobbat — du tog dig igenom {stepCount === 1 ? 'steget' : `alla ${stepCount} steg`}.
                    </p>
                    {isPreview && (
                        <p className="text-xs text-blue-600 mb-4">Förhandsgranskning — inga framsteg har sparats.</p>
                    )}
                    <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-center gap-3">
                        <button
                            onClick={startReview}
                            className="px-6 py-3 border-2 border-[#1A4D4F]/25 text-[#1A4D4F] hover:bg-[#1A4D4F]/5 font-semibold rounded-xl transition-colors"
                        >
                            Gå igenom igen
                        </button>
                        <button
                            onClick={closeOrBack}
                            className="px-6 py-3 bg-[#1A4D4F] hover:bg-[#1A4D4F]/90 text-[#f5f5f5] font-semibold rounded-xl transition-colors"
                        >
                            {finishLabel}
                        </button>
                    </div>
                    <p className="mt-4 text-xs text-gray-500">
                        Du kan läsa innehållet igen och se vad du svarade.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div ref={rootRef} className={NEXILINK_SPACING}>
            {/* Preview banner */}
            {isPreview && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                    <Eye className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-blue-900">Förhandsgranskningsläge</p>
                        <p className="text-xs text-blue-700">Du ser modulen som en kandidat. Inga svar eller framsteg sparas.</p>
                    </div>
                    <button
                        onClick={closeOrBack}
                        className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 hover:bg-blue-50 rounded-lg"
                    >
                        Tillbaka till redigering
                    </button>
                </div>
            )}

            {/* Back button + title — hidden in overlay mode (the overlay chrome
                already shows the title + a close control). */}
            {!onClose && (
                <div className="flex items-center gap-4">
                    {!chromeless && (
                        <button
                            onClick={closeOrBack}
                            className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                    )}
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{data.module_name}</h1>
                        {data.estimated_duration && (
                            <p className="text-sm text-gray-500">Uppskattad tid: {data.estimated_duration}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Progress bar */}
            <div className={`${NEXILINK_CARD_WHITE} p-6`}>
                <ModuleProgressBar
                    steps={data.steps || []}
                    currentStepOrder={currentStepOrder}
                    completedSteps={completedSteps}
                    onStepClick={goToStep}
                    onPrev={goToPreviousStep}
                    onNext={advanceToNextStep}
                    canGoPrev={canGoBack}
                    canGoNext={canGoForward}
                />
            </div>

            {/* Current step — crossfades on step change (see changeStep) */}
            <div className={`${NEXILINK_CARD_WHITE} p-6 transition-opacity duration-200 ease-out ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                {/* key: two consecutive steps of the SAME type would otherwise
                    reuse one component instance and carry the previous step's
                    typed answer and feedback banner across the change. */}
                <StepRenderer
                    key={currentStep.step_order}
                    step={currentStep}
                    savedAnswer={stepAnswers[currentStep.step_order]}
                    caseData={currentStep.step_type === 'case' && currentStep.content?.case_id
                        ? (data.cases || {})[currentStep.content.case_id]
                        : undefined
                    }
                    onSubmit={handleStepSubmit}
                    isSubmitting={isSubmitting}
                    canGoBack={canGoBack}
                    onGoBack={goToPreviousStep}
                />
            </div>
        </div>
    );
};

// ── Step type dispatcher ─────────────────────────────────────────

interface StepRendererProps {
    step: ModuleStep;
    caseData?: any;
    // The answer payload previously submitted for THIS step, if any. Only the
    // renderers that can lose written work read it (#293).
    savedAnswer?: any;
    onSubmit: (answer: any) => Promise<StepResultResponse>;
    isSubmitting: boolean;
    canGoBack: boolean;
    onGoBack: () => void;
}

const StepRenderer: React.FC<StepRendererProps> = ({ step, caseData, savedAnswer, onSubmit, isSubmitting, canGoBack, onGoBack }) => {
    const backProps = { canGoBack, onGoBack };
    switch (step.step_type) {
        case 'info':
            return <InfoStepRenderer step={step} onSubmit={() => onSubmit({})} isSubmitting={isSubmitting} {...backProps} />;
        case 'checkpoint':
            return <CheckpointStepRenderer step={step} savedAnswer={savedAnswer} onSubmit={onSubmit} isSubmitting={isSubmitting} {...backProps} />;
        case 'confirmation':
            return <ConfirmationStepRenderer step={step} onSubmit={onSubmit} isSubmitting={isSubmitting} {...backProps} />;
        case 'quiz':
            return <QuizStepRenderer step={step} savedAnswer={savedAnswer} onSubmit={onSubmit} isSubmitting={isSubmitting} {...backProps} />;
        case 'case':
            return <CaseStepRenderer step={step} caseData={caseData} onSubmit={onSubmit} isSubmitting={isSubmitting} {...backProps} />;
        // #295 — `task` and `file_review` are valid backend step types with no
        // renderer of their own, and legacy/seeded data can carry others. They
        // used to fall through to a literal "Okänd stegtyp: task" shown to the
        // CANDIDATE — an internal type name surfaced as an error to someone
        // taking a course.
        //
        // Both are read-and-continue steps, which is exactly what InfoStepRenderer
        // does, and the server auto-completes anything it doesn't recognise
        // (module_service submit dispatch, `default: StepCompleted = true`), so
        // this agrees with what the backend already believes.
        //
        // The parser no longer EMITS these (normaliseStepType), but courses saved
        // before that fix still exist — no data migration can be relied on here.
        case 'task':
        case 'file_review':
        default:
            return <InfoStepRenderer step={step} onSubmit={() => onSubmit({})} isSubmitting={isSubmitting} {...backProps} />;
    }
};

const ModuleViewer: React.FC<ModuleViewerProps> = (props) => (
    <ModuleErrorBoundary>
        <ModuleViewerInner {...props} />
    </ModuleErrorBoundary>
);

export default ModuleViewer;
